import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowLeft, Loader2, MessageSquarePlus, RotateCw, Search } from 'lucide-react'
import { clearScanHighlight } from '@/shared/office/word'
import type { ChecklistItem, ScanResult, ScanSession } from '@/screens/scan/types'
import { useScanActions } from '@/screens/scan/ScanActionsContext'
import { resultKey, tally, type SelectedHighlight } from '@/screens/scan/lib/sessionDisplay'
import { filterScanResults, RESULT_FILTER_TABS, type ResultFilterTab } from '@/screens/scan/lib/resultFilters'
import { Segmented } from './Segmented'
import { ResultDetailView } from './ResultDetailView'
import { ResultRow } from './ResultRow'

/** Full-screen detail for one scan run: individual results plus batch actions. */
export function SessionDetailView({
  session,
  itemsById,
  onBack,
  onRescanItems,
  onLoadHighlights
}: {
  session: ScanSession
  itemsById: Map<string, ChecklistItem>
  current: boolean
  onBack: () => void
  onRescanItems: (resultIds: string[]) => void
  onLoadHighlights: (resultIds: string[]) => void
}) {
  const { sendRunToChat } = useScanActions()
  const [selectedResult, setSelectedResult] = useState<{ result: ScanResult; name: string } | null>(null)
  const [selectedBlock, setSelectedBlock] = useState<SelectedHighlight | null>(null)
  const [query, setQuery] = useState('')
  const [tab, setTab] = useState<ResultFilterTab>('ALL')
  const counts = useMemo(() => tally(session.results), [session.results])
  const isPending = counts.pending > 0

  const errorIds = useMemo(() => session.results.filter((row) => row._id).map((row) => row._id!), [session.results])

  const nameFor = (row: ScanResult): string => row.name ?? itemsById.get(row.checklistItemId)?.name ?? 'Checklist item'

  const filteredResults = useMemo(
    () => filterScanResults(session.results, { tab, query, nameFor }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session.results, tab, query, itemsById]
  )

  const resolvedItems = useMemo(
    () => session.results.filter((row) => row.status !== 'INITIAL').map((row) => ({ result: row, name: nameFor(row) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session.results, itemsById]
  )

  // This run's detail page is open, so pull its findings now (the history list
  // skips them). Re-runs as the poll resolves more rows; `onLoadHighlights`
  // de-dupes rows already loaded or in flight.
  const resolvedIds = useMemo(
    () => session.results.filter((row) => row._id && row.status !== 'INITIAL').map((row) => row._id!),
    [session.results]
  )
  useEffect(() => {
    if (resolvedIds.length) onLoadHighlights(resolvedIds)
  }, [resolvedIds, onLoadHighlights])

  // Re-resolve the open result against the live list so findings that arrive
  // after the detail page opens (lazy load still in flight) flow into the view
  // instead of leaving it stuck on its loading state.
  const liveSelected = useMemo(() => {
    if (!selectedResult) return null
    const fresh = session.results.find((row) => resultKey(row) === resultKey(selectedResult.result))
    return fresh ? { result: fresh, name: selectedResult.name } : selectedResult
  }, [selectedResult, session.results])

  const onSelectBlock = useCallback((next: SelectedHighlight | null) => {
    setSelectedBlock(next)
    if (!next) void clearScanHighlight()
  }, [])

  useEffect(() => () => void clearScanHighlight(), [])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (selectedResult) setSelectedResult(null)
        else onBack()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onBack, selectedResult])

  if (liveSelected) {
    return (
      <ResultDetailView
        result={liveSelected.result}
        name={liveSelected.name}
        checklistItem={itemsById.get(liveSelected.result.checklistItemId)}
        onBack={() => setSelectedResult(null)}
      />
    )
  }

  return (
    <>
      <div className='ramp-scan-head ramp-scan-head--back-only'>
        <button type='button' className='ramp-link ramp-scan-back' onClick={onBack}>
          <ArrowLeft size={14} /> Scan results
        </button>
      </div>

      <div className='ramp-scan-list'>
        <div className='ramp-scan-toolbar'>
          {errorIds.length ? (
            <button
              type='button'
              className='ramp-scan-rescan-btn'
              disabled={isPending}
              onClick={() => onRescanItems(errorIds)}
            >
              {isPending ? <Loader2 size={13} className='ramp-spin' /> : <RotateCw size={13} />}
              {isPending ? 'Scanning…' : `Rescan`}
            </button>
          ) : null}
          <div className='ramp-scan-search'>
            <Search size={14} className='ramp-scan-search-icon' />
            <input type='text' placeholder='Search' value={query} onChange={(event) => setQuery(event.target.value)} />
          </div>
        </div>

        <Segmented
          className='ramp-seg--full'
          options={RESULT_FILTER_TABS}
          value={tab}
          onChange={(value) => setTab(value as ResultFilterTab)}
        />

        {filteredResults.length === 0 ? (
          <div className='ramp-scan-empty'>No results match your search.</div>
        ) : (
          filteredResults.map((row) => (
            <ResultRow
              key={row._id ?? row.checklistItemId}
              result={row}
              name={nameFor(row)}
              selectedBlock={selectedBlock}
              onSelectBlock={onSelectBlock}
              onView={() => {
                onSelectBlock(null)
                setSelectedResult({ result: row, name: nameFor(row) })
              }}
            />
          ))
        )}
      </div>

      {resolvedItems.length ? (
        <div className='ramp-scan-foot ramp-scan-detail-foot'>
          <button type='button' className='ramp-scan-primary' onClick={() => sendRunToChat(resolvedItems)}>
            <MessageSquarePlus size={14} /> Add all to chat
          </button>
        </div>
      ) : null}
    </>
  )
}
